import { createSlice } from "@reduxjs/toolkit";
import axios from "../../utils/axios";
import { dispatch } from "../store";

// ----------------------------------------------------------------------

const REMESAS_BASE = "/api/v1/remesas";

const initialState = {
  isLoading: false,
  error: null,

  countries: [],
  currenciesByCountry: {}, // { [countryCode]: [currencies] }
  statesByCountry: {}, // { [countryCode]: [states] }
  citiesByState: {}, // { [stateId]: [cities] }
  citiesByCountry: {}, // { [countryCode]: [cities] }

  genders: [],
  typeDocs: [],
  relationships: [],
  purposes: [],
  maritals: [],
  typeBanks: [],
  typeSents: [],
  calcSents: [],
};

const slice = createSlice({
  name: "remesas",
  initialState,
  reducers: {
    // START LOADING
    startLoading(state) {
      state.isLoading = true;
    },

    // STOP LOADING
    stopLoading(state) {
      state.isLoading = false;
    },

    // HAS ERROR
    hasError(state, action) {
      state.isLoading = false;
      state.error = action.payload;
    },

    // COUNTRIES
    getCountriesSuccess(state, action) {
      state.isLoading = false;
      state.countries = action.payload || [];
    },

    // CURRENCIES BY COUNTRY
    getCurrenciesByCountrySuccess(state, action) {
      state.isLoading = false;
      const { countryCode, data } = action.payload;
      state.currenciesByCountry = {
        ...state.currenciesByCountry,
        [countryCode]: data || [],
      };
    },

    // STATES BY COUNTRY
    getStatesByCountrySuccess(state, action) {
      state.isLoading = false;
      const { countryCode, data } = action.payload;
      state.statesByCountry = {
        ...state.statesByCountry,
        [countryCode]: data || [],
      };
    },

    // CITIES BY STATE
    getCitiesByStateSuccess(state, action) {
      state.isLoading = false;
      const { stateId, data } = action.payload;
      state.citiesByState = {
        ...state.citiesByState,
        [stateId]: data || [],
      };
    },

    // CITIES BY COUNTRY
    getCitiesByCountrySuccess(state, action) {
      state.isLoading = false;
      const { countryCode, data } = action.payload;
      state.citiesByCountry = {
        ...state.citiesByCountry,
        [countryCode]: data || [],
      };
    },

    // ENUM LISTS
    getGendersSuccess(state, action) {
      state.isLoading = false;
      state.genders = action.payload || [];
    },

    getTypeDocsSuccess(state, action) {
      state.isLoading = false;
      state.typeDocs = action.payload || [];
    },

    getRelationshipsSuccess(state, action) {
      state.isLoading = false;
      state.relationships = action.payload || [];
    },

    getPurposesSuccess(state, action) {
      state.isLoading = false;
      state.purposes = action.payload || [];
    },

    getMaritalsSuccess(state, action) {
      state.isLoading = false;
      state.maritals = action.payload || [];
    },

    getTypeBanksSuccess(state, action) {
      state.isLoading = false;
      state.typeBanks = action.payload || [];
    },

    getTypeSentsSuccess(state, action) {
      state.isLoading = false;
      state.typeSents = action.payload || [];
    },

    getCalcSentsSuccess(state, action) {
      state.isLoading = false;
      state.calcSents = action.payload || [];
    },
  },
});

export default slice.reducer;

export const { startLoading, stopLoading, hasError } = slice.actions;

export function getCountries() {
  return async () => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await axios.get(`${REMESAS_BASE}/countries`);
      dispatch(slice.actions.getCountriesSuccess(response.data));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function getCurrenciesByCountry(countryCode) {
  return async () => {
    if (!countryCode) return;
    dispatch(slice.actions.startLoading());
    try {
      const response = await axios.get(
        `${REMESAS_BASE}/countries/${countryCode}/currencies`
      );
      dispatch(
        slice.actions.getCurrenciesByCountrySuccess({
          countryCode,
          data: response.data,
        })
      );
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function getStatesByCountry(countryCode) {
  return async () => {
    if (!countryCode) return;
    dispatch(slice.actions.startLoading());
    try {
      const response = await axios.get(
        `${REMESAS_BASE}/countries/${countryCode}/states`
      );
      dispatch(
        slice.actions.getStatesByCountrySuccess({
          countryCode,
          data: response.data,
        })
      );
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function getCitiesByState(stateId) {
  return async () => {
    if (!stateId) return;
    dispatch(slice.actions.startLoading());
    try {
      const response = await axios.get(
        `${REMESAS_BASE}/states/${stateId}/cities`
      );
      dispatch(
        slice.actions.getCitiesByStateSuccess({
          stateId,
          data: response.data,
        })
      );
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function getCitiesByCountry(countryCode, params = {}) {
  return async () => {
    if (!countryCode) return;
    dispatch(slice.actions.startLoading());
    try {
      const response = await axios.get(
        `${REMESAS_BASE}/countries/${countryCode}/cities`,
        { params }
      );
      dispatch(
        slice.actions.getCitiesByCountrySuccess({
          countryCode,
          data: response.data,
        })
      );
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function getGenders() {
  return async () => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await axios.get(`${REMESAS_BASE}/genders`);
      dispatch(slice.actions.getGendersSuccess(response.data));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function getTypeDocs() {
  return async () => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await axios.get(`${REMESAS_BASE}/typedocs`);
      dispatch(slice.actions.getTypeDocsSuccess(response.data));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function getRelationships() {
  return async () => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await axios.get(`${REMESAS_BASE}/relationships`);
      dispatch(slice.actions.getRelationshipsSuccess(response.data));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function getPurposes() {
  return async () => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await axios.get(`${REMESAS_BASE}/purposes`);
      dispatch(slice.actions.getPurposesSuccess(response.data));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function getMaritals() {
  return async () => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await axios.get(`${REMESAS_BASE}/maritals`);
      dispatch(slice.actions.getMaritalsSuccess(response.data));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function getTypeBanks() {
  return async () => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await axios.get(`${REMESAS_BASE}/typebanks`);
      dispatch(slice.actions.getTypeBanksSuccess(response.data));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function getTypeSents() {
  return async () => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await axios.get(`${REMESAS_BASE}/typesents`);
      dispatch(slice.actions.getTypeSentsSuccess(response.data));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function getCalcSents() {
  return async () => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await axios.get(`${REMESAS_BASE}/calcsents`);
      dispatch(slice.actions.getCalcSentsSuccess(response.data));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export async function fetchSenders(params = {}) {
  const response = await axios.get(`${REMESAS_BASE}/senders`, { params });
  return response.data;
}

export async function createSenderApi(data) {
  try {
    const response = await axios.post(`${REMESAS_BASE}/senders`, data);
    return response.data;
  } catch (error) {
    throw error;
  }
}

export async function fetchBeneficiariesBySender(senderId, params = {}) {
  const response = await axios.get(
    `${REMESAS_BASE}/senders/${senderId}/beneficiaries`,
    { params }
  );
  return response.data;
}

export async function createBeneficiaryApi(senderId, data) {
  try {
    const response = await axios.post(
      `${REMESAS_BASE}/senders/${senderId}/beneficiaries`,
      data
    );
    return response.data;
  } catch (error) {
    throw error;
  }
}
export async function updateSenderApi(id, payload) {
  const resp = await axios.patch(`${REMESAS_BASE}/senders/${id}/`, payload);
  return resp.data;
}

export async function updateBeneficiaryApi(id, payload) {
  const resp = await axios.patch(
    `${REMESAS_BASE}/beneficiaries/${id}/`,
    payload
  );
  return resp.data;
}

export const fetchBanks = async (params = {}) => {
  const response = await axios.get(`${REMESAS_BASE}/banks`, { params });
  return response.data;
};

export async function calculateApi(data) {
  const response = await axios.post(`${REMESAS_BASE}/calculate`, data);
  return response.data;
}

export async function rateApi(data) {
  const response = await axios.post(`${REMESAS_BASE}/rate`, data);
  return response.data;
}

export async function sendOrderApi(payload) {
  try {
    const res = await axios.post(`${REMESAS_BASE}/send-order`, payload);
    return res.data;
  } catch (error) {
    const data = error.response?.data;

    const apiError = {
      code:
        data?.code ||
        (error.message === "Network Error"
          ? "NETWORK_ERROR"
          : "UNEXPECTED_ERROR"),
      message:
        data?.message ||
        data?.detail ||
        (error.message === "Network Error"
          ? "Network error, please check your connection."
          : "Unexpected error."),
      errors: data?.errors || null,
      status: error.response?.status,
    };

    throw apiError;
  }
}
